/// <amd-module name="TFS/Admin/Views/ProjectPermissions/ProjectPermissions" />
import React = require("react");
import { IVssPerformanceService } from "VSS/Platform/Performance";

import { Components, VssComponent } from "VSS/Platform/Layout";
import { ObservableValue, IObservableArray, ObservableArray } from "VSS/Core/Observable";
import { Observer } from "VSSUI/Observer";
import { WrappedComponent } from "VSS/Platform/Layout";
import { IIdentity, IdentityType } from "VSSUI/IdentityPicker";
import { GraphSubjectLookupKey, GraphSubjectLookup } from "VSS/Platform/Contracts/Graph";
import { createMarkup } from "TFS/Admin/Common/Util/HtmlString";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { Header, TitleSize } from "VSSUI/Header";

import { AdminTelemetry, AdminFeatureTelemetryLogger } from "TFS/Admin/Common/Util/Telemetry/AdminTelemetry";
import { Area, Feature } from "TFS/Admin/Common/Constants/TelemetryConstants";
import { IPermissionsMessage, IProjectPermissionMembers } from "./Contracts/DataModels";
import { ProjectPermissionsSource } from "Sources/ProjectPermissonsSource";
import { ProjectPermissionsHelper } from "TFS/Admin/Views/ProjectPermissions/Helpers/ProjectPermissionsHelper";
import * as Resources from "TFS/Admin/Views/ProjectPermissions/Resources";
import { ProjectPermissionsTable } from "TFS/Admin/Views/ProjectPermissions/Components/ProjectPermissionsTable";
import { FilterBar } from "VSSUI/FilterBar";
import { Filter } from "VSSUI/Utilities/Filter";
import { KeywordFilterBarItem } from "VSSUI/TextFilterBarItem";
import * as PermissionLevel from "TFS/Admin/Common/Contracts/PermissionLevel";
import { IListBoxItem } from "VSSUI/ListBox";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import * as Constants from "TFS/Admin/Views/ProjectPermissions/Constants/Constants";
import { ParseErrorFromAPI } from "TFS/Admin/Common/Util/CustomErrorHelper";
import { ProjectPermissionsAPIWrapper } from "Sources/ProjectPermissionsAPIWrapper";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { GraphMember } from "VSS/Platform/Contracts/Graph";

const entitiesNameFilterKey: string = "entitiesName";
const dataProviderId = "ms.vss-admin-web.admin-project-permissions-data-provider";

interface IProjectPermissionsData {
    projectId: string;
}
interface IProjectPermissionsState {
    loading: boolean;
}

class ProjectPermissionsComponent extends VssComponent<{}, IProjectPermissionsState> {
    private _data: IProjectPermissionsData;
    private _telemetryLogger: AdminTelemetry = new AdminTelemetry(this.context.pageContext);
    private _permissionsAPIWrapper: ProjectPermissionsAPIWrapper;
    private _restClientSource: ProjectPermissionsSource;
    private _itemProvider: IObservableArray<IProjectPermissionMembers>;
    private _projectDescriptor: string;
    private _continuationToken: string;
    private _filter: Filter;
    private _permissionLevelDefinition: PermissionLevel.PermissionLevelDefinition[];
    private _permissionLevelDisplayNames: IObservableArray<IListBoxItem<string>>;

    private _showAddMemberPanel: ObservableValue<boolean> = new ObservableValue<boolean>(false);
    private _showFilterBar: ObservableValue<boolean> = new ObservableValue<boolean>(false);
    private _messageCardInfo: ObservableValue<IPermissionsMessage | null> = new ObservableValue<IPermissionsMessage | null>(null);

    public constructor(props: {}, context?: any) {
        super(props, context);

        const contributionService = this.context.pageContext.getService<IVssContributionService>("IVssContributionService");
        this._data = contributionService.getData<IProjectPermissionsData>(dataProviderId)!;
        this.state = { loading: true };

        this._itemProvider = new ObservableArray([]);
        this._permissionLevelDisplayNames = new ObservableArray([]);

        this._restClientSource = new ProjectPermissionsSource();
        this._filter = new Filter();
        this._getProjectLevelDefinitions();
        this._getTableItem();

        this._permissionsAPIWrapper = new ProjectPermissionsAPIWrapper(this.context, this._telemetryLogger);
    }

    public render(): JSX.Element {
        if (!this._data) {
            return (
                <div className="admin-project-permissions">
                    <MessageCard className="permissions-content" severity={MessageCardSeverity.Error}>
                        {Resources.DataProviderError}
                    </MessageCard>
                </div>
            );
        }

        return (
            <>
                <div className="admin-project-permissions">
                    <Header
                        title={Resources.PermissionColumnName}
                        titleSize={TitleSize.Large}
                        description={Resources.PermissionsTitleDescription}
                        commandBarItems={this._getCommandBarItems()}
                    />
                    <Observer showFilterBar={this._showFilterBar}>
                        {(props: { showFilterBar: boolean }) => {
                            return (
                                props.showFilterBar && (
                                    <FilterBar filter={this._filter} className="permissions-content">
                                        <KeywordFilterBarItem filterItemKey={entitiesNameFilterKey} />
                                    </FilterBar>
                                )
                            );
                        }}
                    </Observer>
                    <Observer messageCardInfo={this._messageCardInfo}>
                        {(props: { messageCardInfo: IPermissionsMessage | null }) =>
                            props.messageCardInfo !== null && (
                                <MessageCard
                                    className="permissions-content"
                                    severity={props.messageCardInfo.severity}
                                    onDismiss={() => {
                                        this._messageCardInfo.value = null;
                                    }}
                                >
                                    <span dangerouslySetInnerHTML={createMarkup(props.messageCardInfo.name!)} />
                                </MessageCard>
                            )
                        }
                    </Observer>
                    {this._renderTableContent()}
                </div>
                <Observer showAddMemberPanel={this._showAddMemberPanel}>
                    {(props: { showAddMemberPanel: boolean }) =>
                        props.showAddMemberPanel && (
                            <WrappedComponent
                                wrappedType="ms.vss-admin-web.permission-level-add-panel"
                                dependencies={["ms.vss-admin-web.admin-dialogs-permissions-content"]}
                                wrappedProps={{
                                    panelTitle: Resources.AddPanelTitle,
                                    onDismiss: () => {
                                        this._showAddMemberPanel.value = false;
                                    },
                                    pageContext: this.context.pageContext,
                                    onSave: (subjectIdentities: IIdentity[], permissionLevelId: string) => {
                                        this._onSaveMembers(subjectIdentities, permissionLevelId);
                                    },
                                    description: Resources.AddPanelDescription,
                                    identityTypes: [IdentityType.Group, IdentityType.User],
                                    peoplePickerLabel: Resources.PeoplePickerLabel,
                                    permissionLevels: this._permissionLevelDisplayNames,
                                    checkPermissionLevelAssignmentsExistence: (member: IIdentity) => {
                                        return this._permissionsAPIWrapper.permissionLevelAssignmentsExistence(member, this._data.projectId);
                                    }
                                }}
                            />
                        )
                    }
                </Observer>
            </>
        );
    }

    public componentDidMount(): void {
        const performanceService: IVssPerformanceService = this.context.pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
        performanceService.markTimeToInteractive(Area.Admin, Feature.ProjectPermissions); // TODO - add telemetry for page load
    }

    private _renderTableContent(): JSX.Element {
        if (this.state.loading) {
            return <Spinner className={"project-permissions-spinner"} label={Resources.Loading} size={SpinnerSize.large} />;
        }

        return (
            <ProjectPermissionsTable
                pageContext={this.context.pageContext}
                itemProvider={this._itemProvider}
                filter={this._filter}
                filterItemKey={entitiesNameFilterKey}
                permissionLevelDefinition={this._permissionLevelDefinition}
                permissionLevelDisplayNames={this._permissionLevelDisplayNames}
                startActionCapture={this._startActionCapture}
                fetchMoreItems={this._fetchAssignmentItems}
                updatePermissionLevel={this._updatePermissionLevelForSubject}
            />
        );
    }

    //TODO: Need to update based on final figma
    private _getCommandBarItems = () => {
        return [
            {
                id: "filter",
                iconProps: {
                    iconName: "Filter"
                },
                ariaLabel: "Filter button",
                onActivate: () => {
                    this._showFilterBar.value = !this._showFilterBar.value;
                },
                subtle: true
            },
            {
                id: "addmember",
                iconProps: { iconName: "Add" },
                ariaLabel: "Add People & Groups",
                text: "Add People & Groups",
                onActivate: () => {
                    this._showAddMemberPanel.value = true;
                },
                isPrimary: true,
                disabled: !this._data.projectId
            }
        ];
    };

    private _startActionCapture = (action: string, properties: { [key: string]: any }): AdminFeatureTelemetryLogger => {
        return this._telemetryLogger.startActionCapture(Feature.ProjectPermissions, Feature.ProjectPermissions, action, properties);
    };

    private async _onSaveMembers(subjectIdentities: IIdentity[], permissionLevelDefinitionId: string) {
        var actionTelemetryLogger = this._startActionCapture("Add member(s) button clicked", {});

        let resultMembers: any;

        try {
            if (subjectIdentities.length > 0) {
                resultMembers = await this.assignPermissionLevelToIdentities(subjectIdentities, permissionLevelDefinitionId, this._data.projectId);
                this._messageCardInfo.value = ProjectPermissionsHelper.parseAddMembersResult(resultMembers);
            } else {
                actionTelemetryLogger.captureError("Invalid data");
            }
        } catch (error) {
            actionTelemetryLogger.captureError(error || error.message);
            this._messageCardInfo.value = { name: error || error.message, severity: MessageCardSeverity.Error } as IPermissionsMessage;
        }

        this._showAddMemberPanel.value = false;
    }

    private async assignPermissionLevelToIdentities(
        subjectIdentities: IIdentity[],
        permissionLevelDefinitionId: string,
        resourceId: string
    ): Promise<any> {
        const success: any[] = [];
        const failed: any[] = [];

        const defaultTeamDescriptor = await this._permissionsAPIWrapper.getDefaultTeamDescriptor(this._data.projectId);
        await Promise.all(
            subjectIdentities.map(async identity => {
                await this._permissionsAPIWrapper.createEntity(identity!, defaultTeamDescriptor!).then(async entitySuccess => {
                    let permissionLevelAssignment: PermissionLevel.PermissionLevelAssignment | null;
                    if (
                        entitySuccess &&
                        (permissionLevelAssignment = await this._permissionsAPIWrapper.assignPermission(
                            identity,
                            permissionLevelDefinitionId,
                            resourceId
                        )) !== null
                    ) {
                        success.push(identity.subjectDescriptor);
                        this._itemProvider.push({ member: identity, permissionLevelAssignment: permissionLevelAssignment });
                    } else {
                        failed.push(identity.subjectDescriptor);
                    }
                });
            })
        );

        return Promise.resolve({ success, failed });
    }

    private _updatePermissionLevelForSubject = async (
        subjecDescriptor: string,
        newDefinitionId: string,
        previousDefinitionId: string
    ): Promise<PermissionLevel.PermissionLevelAssignment> => {
        return this._restClientSource.updatePermissionLevelAssignment(
            this.context,
            subjecDescriptor,
            this._data.projectId,
            previousDefinitionId,
            newDefinitionId
        );
    };

    private _getProjectLevelDefinitions = async () => {
        if (!this._permissionLevelDefinition) {
            var projectLevelDefinitions = await this._restClientSource.getPermissionLevelDefinitionsForProject(this.context);

            this._permissionLevelDefinition = projectLevelDefinitions;
        }

        if (this._permissionLevelDisplayNames && this._permissionLevelDisplayNames.length == 0) {
            this._permissionLevelDefinition.forEach(o => {
                this._permissionLevelDisplayNames.push({ id: o.id, text: o.name });
            });
        }
    };

    private _getTableItem = async () => {
        if (!this._projectDescriptor || this._projectDescriptor.length < 1) {
            const projectDescriptor = await this._restClientSource.getDescriptor(this.context, this._data.projectId);
            if (projectDescriptor) {
                this._projectDescriptor = projectDescriptor.value;
            }
        }

        if (this._projectDescriptor && this._projectDescriptor.length > 0) {
            var pagedGroups = await this._restClientSource.getProjectGroupList(this.context, this._projectDescriptor);

            for (var group in pagedGroups.graphGroups) {
                this._itemProvider.push({
                    member: this._castGraphMemberToIIdentity(pagedGroups.graphGroups[group]),
                    permissionLevelAssignment: {
                        _links: "",
                        isInherited: false,
                        permissionLevelDefinitionId: "",
                        resourceId: this._data.projectId,
                        subject: { identifier: "", subjectType: "" }
                    }
                });
            }

            this.setState({ loading: false });
        }

        // Fetch PLAssignment entities
        this._fetchAssignmentItems();
    };

    private _fetchAssignmentItems() {
        var actionTelemetryLogger = this._startActionCapture("Look up permission level assignments", {});

        this._restClientSource.getProjectAssignmentsList(this.context, this._data.projectId, this._continuationToken).then(
            assignmentsList => {
                if (assignmentsList && assignmentsList.permissionLevelAssignments && assignmentsList.permissionLevelAssignments.length > 0) {
                    var lookupKeys: GraphSubjectLookupKey[] = [];
                    var assignmentLookupList: PermissionLevel.PermissionLevelAssignment[] = [];
                    this._continuationToken = assignmentsList.continuationToken;

                    for (const assignment of assignmentsList.permissionLevelAssignments) {
                        lookupKeys.push({ descriptor: assignment.subject });
                        assignmentLookupList.push(assignment);

                        if (lookupKeys.length >= Constants.AssignmentsLookUpBatchSize) {
                            this._lookUpBatchUsers(lookupKeys, assignmentLookupList);
                            lookupKeys = [];
                            assignmentLookupList = [];
                        }
                    }
                    this._lookUpBatchUsers(lookupKeys, assignmentLookupList);
                }
            },
            error => {
                actionTelemetryLogger.captureError(ParseErrorFromAPI(error));
            }
        );
    }

    private _lookUpBatchUsers(lookupKeys: GraphSubjectLookupKey[], assignmentLookupList: PermissionLevel.PermissionLevelAssignment[]) {
        var actionTelemetryLogger = this._startActionCapture("Look up subjects", {});

        if (lookupKeys.length > 0) {
            this._lookUpSubject({ lookupKeys: lookupKeys }).then(
                graphSubjects => {
                    assignmentLookupList.forEach(assign => {
                        let descriptor: any = assign.subject;

                        if (graphSubjects && graphSubjects.hasOwnProperty(descriptor)) {
                            const subject = graphSubjects[descriptor];

                            if (subject) {
                                this._itemProvider.push({
                                    member: this._castGraphMemberToIIdentity(graphSubjects[descriptor]),
                                    permissionLevelAssignment: assign
                                });
                            }
                        }
                    });
                },
                error => {
                    actionTelemetryLogger.captureError(ParseErrorFromAPI(error));
                }
            );
        }
    }

    private _lookUpSubject(lookupKeys: GraphSubjectLookup): Promise<{ [key: string]: any }> {
        return this._restClientSource.getSubjects(this.context, lookupKeys);
    }

    private _castGraphMemberToIIdentity(member: GraphMember): IIdentity {
        let newIdentity: IIdentity = {} as IIdentity;

        newIdentity.localId = member.originId;
        newIdentity.displayName = member.displayName;
        newIdentity.image = member._links && member._links.avatar && member._links.avatar.href ? member._links.avatar.href : undefined;
        newIdentity.mail = member.mailAddress;
        newIdentity.subjectDescriptor = member.descriptor;
        newIdentity.entityType = member.subjectKind;

        return newIdentity;
    }
}

Components.add("ms.vss-admin-web.project-permissions-component", ProjectPermissionsComponent);
